import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { theme } from "../../theme.js";
import { API_URL } from "../../utils/constants.js";
import LoadingOverlay from "../LoadingOverlay.jsx";

import BarList from "./ChartBarList.jsx";
import ChartPie from "./ChartPie.jsx";
import ChartBar from "./ChartBar.jsx";
import ChartTable from "./ChartTable.jsx";

export default function Analytics() {
  const { org_id } = useParams();
  const [ticketsData, setTicketsData] = useState([]);
  const [bibsData, setBibsData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedRace, setSelectedRace] = useState("all");

  useEffect(() => {
    if (!org_id) {
      console.error("Organization ID not found in URL");
      setLoading(false);
      return;
    }

    const fetchEventsData = async () => {
      try {
        // Fetch events for this organization
        const eventsResponse = await fetch(`${API_URL}/api/events?organizer_id=${org_id}`);
        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          setEvents(Array.isArray(eventsData) ? eventsData : []);
        }
      } catch (err) {
        console.error("Error fetching events data:", err);
      }
    };

    fetchEventsData();
  }, [org_id]);

  useEffect(() => {
    if (!org_id) {
      return;
    }

    setSelectedRace("all"); // Reset race filter when event changes

    const fetchRacesData = async () => {
      try {
        let racesUrl;
        if (selectedEvent !== "all") {
          // Fetch races for the selected event
          racesUrl = `${API_URL}/api/races?event_id=${selectedEvent}`;
        } else {
          // Fetch all races for the organization
          racesUrl = `${API_URL}/api/races?organizer_id=${org_id}`;
        }
        
        const racesResponse = await fetch(racesUrl);
        if (racesResponse.ok) {
          const racesData = await racesResponse.json();
          setRaces(Array.isArray(racesData) ? racesData : []);
        }
      } catch (err) {
        console.error("Error fetching races data:", err);
      }
    };

    fetchRacesData();
  }, [org_id, selectedEvent]);

  useEffect(() => {
    if (!org_id) {
      console.error("Organization ID not found in URL");
      setLoading(false);
      return;
    }

    const fetchAnalyticsData = async () => {
      try {
        // Build query parameters
        const params = new URLSearchParams();
        if (selectedEvent !== "all") params.append("event_id", selectedEvent);
        if (selectedRace !== "all") params.append("race_id", selectedRace);

        const [ticketsResponse, bibsResponse, revenueResponse] = await Promise.all([
          fetch(`${API_URL}/api/data/races-prices-tickets/${org_id}?${params}`).catch(err => ({ ok: false, error: err })),
          fetch(`${API_URL}/api/data/bibs-participants-payments/${org_id}?${params}`).catch(err => ({ ok: false, error: err })),
          fetch(`${API_URL}/api/data/revenue-by-type/${org_id}?${params}`).catch(err => ({ ok: false, error: err }))
        ]);
        
        // Handle each response
        let ticketsData = [];
        let bibsDataLocal = [];
        let revenueDataLocal = [];
        
        if (ticketsResponse.ok) {
          const result = await ticketsResponse.json();
          ticketsData = Array.isArray(result) ? result : [];
        } else {
          console.warn("Failed to fetch tickets data:", ticketsResponse.error);
        }
        
        if (bibsResponse.ok) {
          const result = await bibsResponse.json();
          bibsDataLocal = Array.isArray(result) ? result : [];
        } else {
          console.warn("Failed to fetch bibs data:", bibsResponse.error);
        }
        
        if (revenueResponse.ok) {
          const result = await revenueResponse.json();
          revenueDataLocal = Array.isArray(result) ? result : [];
        } else {
          console.warn("Failed to fetch revenue data:", revenueResponse.error);
        }
        
        setTicketsData(ticketsData);
        setBibsData(bibsDataLocal);
        setRevenueData(revenueDataLocal);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching analytics data:", err);
        // Don't set error state, just load with empty data
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [org_id, selectedEvent, selectedRace]);

  if (loading) {
    return <LoadingOverlay isVisible={true} message="Chargement des graphiques..." />;
  }

  return (
    <div style={styles.mainLayout}>
      {/* Filter Section */}
      <div style={styles.filterContainer}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Événement:</label>
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Tous les événements</option>
            {events.map((event) => (
              <option key={event.id_bdd} value={event.id_bdd}>
                {event.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Course:</label>
          <select
            value={selectedRace}
            onChange={(e) => setSelectedRace(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Toutes les courses</option>
            {races.map((race) => (
              <option key={race.id_bdd} value={race.id_bdd}>
                {race.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <BarList data={ticketsData} />
      <ChartPie data={revenueData} />
      <div style={styles.tableRow}>
        <ChartTable data={bibsData} />
      </div>
    </div>
  );
}

const styles = {
  mainLayout: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1.5rem",
    padding: "1.5rem",
    backgroundColor: "#F9FAFB",
    minHeight: "100vh",
  },
  filterContainer: {
    gridColumn: "1 / -1",
    display: "flex",
    gap: "2rem",
    padding: "1.5rem",
    backgroundColor: "white",
    borderRadius: "0.5rem",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    marginBottom: "1rem",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    flex: 1,
    maxWidth: "300px",
  },
  filterLabel: {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#374151",
  },
  filterSelect: {
    padding: "0.5rem 0.75rem",
    borderRadius: "0.375rem",
    border: "1px solid #D1D5DB",
    fontSize: "0.875rem",
    backgroundColor: "white",
    cursor: "pointer",
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: "#9CA3AF",
    },
    "&:focus": {
      outline: "none",
      borderColor: "#3B82F6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  chartsRow: {
    display: "contents",
  },
  tableRow: {
    gridColumn: "1 / -1",
  },
  container: {
    backgroundColor: "#F9FAFB",
    minHeight: "100vh",
    padding: "1.5rem",
  },
};
